import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { Plus, LogOut, ArrowLeft, Search, Shield, Users, Globe } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { MunicipalityForm } from '@/components/MunicipalityForm';
import { MunicipalityCard } from '@/components/MunicipalityCard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ScreensaverManager } from '@/components/ScreensaverManager';
import { GlobalScreensaverManager } from '@/components/GlobalScreensaverManager';
// import { TotemLogsPanel } from '@/components/TotemLogsPanel';
import { AdminManagement } from '@/components/AdminManagement';

interface HeroData {
  name: string;
  image_url: string;
  click_url: string;
}

interface Municipality {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  heroes_data: HeroData[];
  municipality_web_url: string;
  newsletter_url: string;
  carpetania_url: string;
  enjoy_url: string;
}

const Admin = () => {
  const { isSuperAdmin, isAdmin, assignedMunicipalities, loading: roleLoading } = useUserRole();
  const { user, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMunicipality, setEditingMunicipality] = useState<Municipality | null>(null);
  const [showScreensaverManager, setShowScreensaverManager] = useState(false);
  const [managingScreensaver, setManagingScreensaver] = useState<Municipality | null>(null);
  const [showAdminManagement, setShowAdminManagement] = useState(false);
  const [showGlobalScreensaver, setShowGlobalScreensaver] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string; name: string }>({
    open: false,
    id: '',
    name: ''
  });
  const [reloadDialog, setReloadDialog] = useState<{
    open: boolean;
    id: string;
    name: string;
  }>({
    open: false,
    id: '',
    name: '',
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Solo redirigir a /auth si NO hay usuario autenticado
    if (!roleLoading && !authLoading && !user) {
      navigate('/auth');
    }
  }, [user, roleLoading, authLoading, navigate]);

  const loadMunicipalities = useCallback(async () => {
    setLoading(true);
    try {
      let data = await api.municipalities.list();

      // If not superadmin, filter by assigned municipalities
      if (!isSuperAdmin && assignedMunicipalities.length > 0) {
        data = data.filter((m: any) => assignedMunicipalities.includes(m.id));
      }

      setMunicipalities(data as any || []);
    } catch (error) {
      console.error('Error loading municipalities:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los municipios",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, assignedMunicipalities, toast]);

  useEffect(() => {
    if (isAdmin) {
      loadMunicipalities();
    }
  }, [isAdmin, loadMunicipalities]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteDialog({ open: true, id, name });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.municipalities.delete(deleteDialog.id);

      toast({
        title: "Municipio eliminado",
        description: `"${deleteDialog.name}" ha sido eliminado exitosamente`
      });

      loadMunicipalities();
    } catch (error) {
      console.error('Error deleting municipality:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el municipio",
        variant: "destructive"
      });
    } finally {
      setDeleteDialog({ open: false, id: '', name: '' });
    }
  };

  const handleReloadDevices = (municipality: Municipality) => {
    setReloadDialog({
      open: true,
      id: municipality.id,
      name: municipality.name,
    });
  };

  const handleReloadConfirm = async () => {
    try {
      await api.municipalities.requestReload(reloadDialog.id);
      toast({
        title: "Señal de recarga enviada",
        description: `Los dispositivos de "${reloadDialog.name}" se recargarán en menos de 5 segundos.`,
      });
    } catch (error) {
      console.error('Error requesting device reload:', error);
      toast({
        title: "Error",
        description: "No se pudo enviar la señal de recarga",
        variant: "destructive",
      });
    } finally {
      setReloadDialog({ open: false, id: '', name: '' });
    }
  };

  const handleEdit = (municipality: Municipality) => {
    setEditingMunicipality(municipality);
    setShowForm(true);
  };

  const handleFormClose = useCallback(() => {
    setShowForm(false);
    setEditingMunicipality(null);
    loadMunicipalities();
  }, [loadMunicipalities]);

  const handleManageScreensaver = (municipality: Municipality) => {
    setManagingScreensaver(municipality);
    setShowScreensaverManager(true);
  };

  const handleScreensaverManagerClose = () => {
    setShowScreensaverManager(false);
    setManagingScreensaver(null);
  };

  if (roleLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  // Usuario autenticado pero sin permisos de admin
  if (user && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-muted-foreground" />
              Sin acceso
            </CardTitle>
            <CardDescription>
              Tu cuenta aún no tiene permisos de administrador.
              Contacta con un superadministrador para que te asigne un municipio.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Usuario: {user.email}
            </p>
            <Button onClick={handleSignOut} variant="outline" className="w-full">
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar Sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No hay usuario autenticado
  if (!user) {
    return null;
  }

  if (showForm) {
    return (
      <MunicipalityForm
        key={editingMunicipality?.id ?? 'new'}
        municipality={editingMunicipality}
        onClose={handleFormClose}
        isSuperAdmin={isSuperAdmin}
      />
    );
  }

  if (showAdminManagement) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
        <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-6">
            <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="flex h-12 md:h-20 items-center rounded-2xl bg-primary px-4 text-lg md:text-2xl font-black text-primary-foreground">Córdoba</div>
                <div>
                  <h1 className="text-xl md:text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Gestión de Administradores
                  </h1>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">Córdoba</p>
                </div>
              </div>
              <Button
                onClick={() => setShowAdminManagement(false)}
                variant="outline"
                size="sm"
                className="w-full md:w-auto"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
          <AdminManagement onClose={() => setShowAdminManagement(false)} />
        </div>
      </div>
    );
  }

  if (showGlobalScreensaver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
        <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-6">
            <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="flex h-12 md:h-20 items-center rounded-2xl bg-primary px-4 text-lg md:text-2xl font-black text-primary-foreground">Córdoba</div>
                <div>
                  <h1 className="text-xl md:text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Salvapantallas Global
                  </h1>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">Para todos los municipios</p>
                </div>
              </div>
              <Button
                onClick={() => setShowGlobalScreensaver(false)}
                variant="outline"
                size="sm"
                className="w-full md:w-auto"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
          <GlobalScreensaverManager />
        </div>
      </div>
    );
  }

  if (showScreensaverManager && managingScreensaver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
        <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-6">
            <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="flex h-12 md:h-20 items-center rounded-2xl bg-primary px-4 text-lg md:text-2xl font-black text-primary-foreground">Córdoba</div>
                <div>
                  <h1 className="text-xl md:text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Salvapantallas
                  </h1>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">{managingScreensaver.name}</p>
                </div>
              </div>
              <Button
                onClick={handleScreensaverManagerClose}
                variant="outline"
                size="sm"
                className="w-full md:w-auto"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
          <ScreensaverManager
            municipalityId={managingScreensaver.id}
            municipalityName={managingScreensaver.name}
          />
        </div>
      </div>
    );
  }

  // Filter municipalities that the user can see
  const visibleMunicipalities = municipalities.filter((m) =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Header con branding */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-6">
          <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="flex h-12 md:h-20 items-center rounded-2xl bg-primary px-4 text-lg md:text-2xl font-black text-primary-foreground">Córdoba</div>
              <div>
                <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3">
                  <h1 className="text-xl md:text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Panel de Administración
                  </h1>
                  <Badge variant={isSuperAdmin ? 'default' : 'secondary'} className="flex items-center gap-1 w-fit">
                    <Shield className="h-3 w-3" />
                    {isSuperAdmin ? 'Superadmin' : 'Admin'}
                  </Badge>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mt-1">Córdoba</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              {isSuperAdmin && (
                <>
                  <Button
                    onClick={() => setShowGlobalScreensaver(true)}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    <Globe className="mr-2 h-4 w-4" />
                    Salvapantallas Global
                  </Button>
                  <Button
                    onClick={() => setShowAdminManagement(true)}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Gestionar Admins
                  </Button>
                </>
              )}
              <Button
                onClick={handleSignOut}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
        <Card className="border-2 shadow-xl">
          <CardHeader className="border-b bg-muted/30 p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex flex-col gap-2">
                <CardTitle className="text-xl md:text-2xl">Municipios</CardTitle>
                <CardDescription className="text-sm md:text-base">
                  {isSuperAdmin
                    ? 'Gestiona todos los municipios y sus configuraciones'
                    : `Gestionando ${assignedMunicipalities.length} municipio(s) asignado(s)`
                  }
                </CardDescription>
              </div>
              {isSuperAdmin && (
                <Button
                  onClick={() => {
                    setEditingMunicipality(null);
                    setShowForm(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Añadir Municipio
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 md:p-8">
            {/* Buscador */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar municipio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-40 w-full" />
                    <div className="p-6 space-y-3">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : visibleMunicipalities.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto mb-4 flex h-24 w-fit items-center rounded-3xl bg-primary px-8 text-3xl font-black text-primary-foreground">Córdoba</div>
                <h3 className="text-xl font-semibold mb-2">
                  {searchQuery ? 'No se encontraron municipios' : 'No hay municipios'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery
                    ? 'Prueba con otro término de búsqueda'
                    : isSuperAdmin
                      ? 'Comienza agregando tu primer municipio'
                      : 'No tienes municipios asignados'
                  }
                </p>
                {isSuperAdmin && !searchQuery && (
                  <Button
                    onClick={() => {
                      setEditingMunicipality(null);
                      setShowForm(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Añadir Municipio
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleMunicipalities.map((municipality) => (
                  <MunicipalityCard
                    key={municipality.id}
                    municipality={municipality}
                    onEdit={handleEdit}
                    onDelete={isSuperAdmin ? handleDeleteClick : undefined}
                    onManageScreensaver={handleManageScreensaver}
                    onReloadDevices={handleReloadDevices}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Panel de Logs del Tótem - Deshabilitado temporalmente
        <div className="mt-8">
          <TotemLogsPanel municipalityIds={isSuperAdmin ? undefined : assignedMunicipalities} />
        </div>
        */}
      </div>

      {/* Confirm delete dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
        title="¿Eliminar municipio?"
        description={`¿Estás seguro de que deseas eliminar "${deleteDialog.name}"? Esta acción no se puede deshacer.`}
        onConfirm={handleDeleteConfirm}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
      />

      <ConfirmDialog
        open={reloadDialog.open}
        onOpenChange={(open) => setReloadDialog({ ...reloadDialog, open })}
        title="¿Recargar todos los dispositivos?"
        description={`Se recargará completamente la web de todos los dispositivos configurados para "${reloadDialog.name}".`}
        onConfirm={handleReloadConfirm}
        confirmText="Enviar recarga"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default Admin;
